/**
* Konva Image Cropping Plugin.
*
* cropTransform - position of the cropped image. x,y,width,height and rotation
* cropEnd - exit cropping
* cropStart - enter cropping
*/

import Pipeline from "./Pipeline";
import defaults from "./_defaults";

(function (Konva) {

    /**
     * Decomposes standard 2x2 matrix into transform componentes
     * @static
     * @memberOf geometry
     * @param  {Array} a transformMatrix
     * @return {Object} Components of transform
     */
    function qrDecompose(a) {
        let angle = Math.atan2(a[1], a[0]),
            denom = Math.pow(a[0], 2) + Math.pow(a[1], 2),
            scaleX = Math.sqrt(denom),
            scaleY = (a[0] * a[3] - a[2] * a[1]) / scaleX;

        return {
            rotation: angle / (Math.PI / 180),
            scaleX: scaleX,
            scaleY: scaleY,
            x: a[4],
            y: a[5]
        };
    }

    function getGroupCoords(object, group) {
        let mB = object.getAbsoluteTransform().getMatrix();
        let mX = group.getAbsoluteTransform().getMatrix();

        //possible to replace with mB * mX.invert()
        let M = mB[0], N = mB[1], O = mB[2], P = mB[3], R = mB[4], S = mB[5],
            A = mX[0], B = mX[1], C = mX[2], D = mX[3], E = mX[4], F = mX[5],
            AD = A * D,
            BC = B * C,
            G = (C * N - M * D) / (BC - AD),
            H = (A * N - M * B) / (AD - BC),
            I = (C * P - O * D) / (BC - AD),
            J = (A * P - O * B) / (AD - BC),
            K = (C * (S - F) + D * (E - R)) / (BC - AD),
            L = (A * (S - F) + B * (E - R)) / (AD - BC);


        let matrix = [G, H, I, J, K, L],
            options = qrDecompose(matrix);

        return options;
    }

    function getCropHandlerAttributes(left = true) {
        return {
            offsetX: 0,
            shadowEnabled: true,
            shadowColor: 'black',
            sceneFunc(context, shape) {
                context.beginPath();
                context.shadowBlur = 15;
                context.shadowOffsetX = left ? 8 : -8;
                context.lineWidth = 0;
                context.fillStyle = 'rgba(255, 255, 255, 1)';
                context.shadowColor = 'rgba(0, 0, 0, .2)';
                if (left) {
                    context.arc(0, 25, 50, 1.5 * Math.PI, .5 * Math.PI, false);
                } else {
                    context.arc(0, 25, 50, .5 * Math.PI, 1.5 * Math.PI, false);
                }
                context.closePath();
                context.fillShape(shape);

                context.strokeStyle = '#e5e7eb';
                context.fillStyle = 'rgba(255, 255, 255, 0)';
                context.lineWidth = 2;
                context.shadowColor = 'transparent';
                context.beginPath();
                context.moveTo(left ? 19 : -19, 16);
                context.lineTo(left ? 9 : -9, 26);
                context.lineTo(left ? 19 : -19, 36);
                context.stroke();

                context.beginPath();
                context.moveTo(left ? 25 : -25, 16);
                context.lineTo(left ? 35 : -35, 26);
                context.lineTo(left ? 25 : -25, 36);
                context.stroke();

                context.beginPath();
                context.moveTo(0, ((shape.y() * -1) + 25));
                context.lineTo(0, (shape.y() + 25));
                context.stroke();
            },
            hitFunc: function (context) {
                context.beginPath();
                context.rect(0, -40, left ? 70 : -70, 130);
                context.closePath();
                context.fillStrokeShape(this);
            },
        }
    }

    Konva.Image.prototype.setCropTransform = function (value) {
        if (value === false) {
            delete this._cropElement;
            return;
        }
        if (!this._cropElement) {
            this._cropElement = new Konva.Shape();
        }
        this._cropElement.setAttrs(value);
        this._cropElement.setAttrs({
            offsetX: 0,
            offsetY: 0
        });
    };

    Konva.Image.prototype.getCropTransform = function () {
        return this._cropElement && this._cropElement.getAttrs();
    };

    Konva.Image.prototype.cropTransform = function (value) {
        if (value) {
            this.setCropTransform(value);
        } else {
            return this.getCropTransform();
        }
    };

    Konva.Image.prototype.cropEnd = function (context) {
        if (this.cropImage) {
            this.transformer.destroy();
            this.off('transform', this.resizAndCropUpdateBinded);
            this.cropImage.remove();
            delete this.cropImage;
            delete this.transformer;
            this.getLayer().batchDraw();
        }
    };

    Konva.Image.prototype.cropUpdate = function (config) {
        let options = getGroupCoords(this.cropImage, this);
        let waveImage = this.getStage().findOne(`#wave_image_${config.idx}`);
        this.cropTransform(options);

        config.cropOptions = {
            x: options.x,
            width: this.width(),
            xPercent: options.x / config.initialWidth,
            widthPercent: this.width() / config.initialWidth
        };

        if (waveImage) {
            waveImage.clip({...Pipeline._getClipOptions(config)});
        }

        this.getLayer().batchDraw();
    };

    Konva.Image.prototype.resize = function () {
        this.setAttrs({
            scaleX: 1,
            scaleY: 1,
            width: this.width() * this.scaleX(),
            height: this.height() * this.scaleY()
        });
    };

    Konva.Image.prototype.cropReset = function (context) {
        if (this.cropImage) {
            this.cropEnd();
        }
        this.setCropTransform(false);
        this.getLayer().batchDraw();
    };

    Konva.Image.prototype.cropStart = function (config) {
        if (this.cropImage) {
            return;
        }
        if (!this._cropElement) {
            this.cropTransform({
                x: 0,
                y: 0,
                width: this.width(),
                height: this.height(),
                rotation: 0
            })
        }
        let layer = this.getLayer(),
            transform = this.getAbsoluteTransform(),
            transform2 = this._cropElement.getAbsoluteTransform(),
            transform0 = layer.getAbsoluteTransform(),
            options = qrDecompose(transform0.copy().invert().multiply(transform).multiply(transform2).getMatrix());

        this.cropImage = new Konva.Image({
            stroke: this.stroke(),
            strokeWidth: this.strokeWidth(),
            image: this.image(),
            opacity: 0.5,
            draggable: false
        });
        this.cropImage.isCroppingElement = true;
        this.cropImage.setAttrs(options);
        this.cropImage.setAttrs({
            width: this._cropElement.width(),
            height: this._cropElement.height(),
        });

        layer.add(this.cropImage);

        const hasTouch = 'ontouchstart' in window;

        this.transformer = new Konva.Transformer({
            borderEnabled: false,
            anchorSize: 50,
            anchorCornerRadius: 50,
            rotateEnabled: false,
            enabledAnchors: ['middle-left', 'middle-right'],
            // limit transformer bounds
            boundBoxFunc: (oldBox, newBox) => {
                const minWidth = 20;
                const minX = config.initialX;

                if (newBox.width < (minWidth + 0.5)) {
                    return oldBox;
                }

                if (newBox.x < (minX - 0.5)) {
                    return oldBox;
                }

                let maxWidth = config.initialWidth - (newBox.x - minX);
                if (newBox.width > maxWidth) {
                    newBox.width = maxWidth;
                }

                return newBox;
            },
        }).attachTo(this);

        this.transformer.update = () => {
            Konva.Transformer.prototype.update.call(this.transformer);
            this.transformer.findOne('.middle-left').setAttrs(getCropHandlerAttributes());
            this.transformer.findOne('.middle-right').setAttrs(getCropHandlerAttributes(false));
        }

        layer.add(this.transformer);

        this.cropUpdateBinded = this.cropUpdate.bind(this);

        this.resizAndCropUpdateBinded = function () {
            this.resize();
            this.cropUpdate(config)
        }.bind(this);

        this.resize();
        this.cropUpdate(config);
        this.on('transform', this.resizAndCropUpdateBinded);

        layer.batchDraw();
    };

    Konva.Image.prototype._sceneFunc = function (context) {
        let width = this.width(),
            height = this.height(),
            image = this.image(),
            cropWidth,
            cropHeight,
            params;

        context.save();
        context.beginPath();
        context.rect(0, 0, width, height);
        context.closePath();
        context.clip();
        if (this.hasFill() || this.hasStroke()) {
            context.fillStrokeShape(this);
        }

        if (image) {

            if (this._cropElement) {
                context.save();
                width = this._cropElement.width();
                height = this._cropElement.height();
                let transform = this._cropElement.getAbsoluteTransform();
                let m = transform.getMatrix();
                context.transform(m[0], m[1], m[2], m[3], m[4], m[5]);
            }

            cropWidth = this.cropWidth();
            cropHeight = this.cropHeight();
            if (cropWidth && cropHeight) {
                params = [
                    image,
                    this.cropX(),
                    this.cropY(),
                    cropWidth,
                    cropHeight,
                    0,
                    0,
                    width,
                    height
                ];
            } else {
                params = [image, 0, 0, width, height];
            }

            context.drawImage.apply(context, params);

            if (this._cropElement) {
                context.restore();
            }
        }
        context.strokeShape(this);
        context.restore();
    };
})(Konva)
